/* eslint-disable */
import React, { useState, useCallback, useEffect, useRef } from "react";
import useUserContext from "../../../hooks/useUserContext";
import { useNavigate } from "react-router-dom";
import { debounce } from "../../../services/util";
import { MAGIC_NUMBERS, COHATCH_LOCATIONS } from "../../../constant";
import getApplications from "../../scholarship_applications/requests/getApplications";
import getAllImpactStats from "../dashboard/getAllImpactStats";
import notify from "../../../services/toast";
import { capitalizeFirstLetter } from "../../../services/util";
import { format } from "date-fns";
import { Box, useTheme } from "@mui/material";
import { tokens } from "../../TestAdmin/Themes";
import useTitle from "../../../hooks/useTitle";
import { DataGrid, GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport } from "@mui/x-data-grid";
import { MdLocationPin } from "react-icons/md";
import { PieChart, Pie, Cell, Label, Tooltip } from "recharts";
import getMarketLevelData from "./getMarketLevelData";
import deleteApplication from "../../scholarship_applications/requests/deleteApplication";
import cpLogo from "../../../assets/CommunityPartnerLogo.png";

function MarketLevel() {
	useTitle("Market Level");
	const { userState } = useUserContext();
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const navigate = useNavigate();
	const [apps, setApps] = useState([]);
	const [marketData, setMarketData] = useState([]);
	const [filteredApps, setFilteredApps] = useState([]);
	const [dashstats, setStats] = useState({});
	const [currentMarket, setCurrentMarket] = useState(userState.market);
	const [years, setYears] = useState(["All"]);
	const [selectedYear, setSelectedYear] = useState("All");
	const [filteredPendingApps, setFilteredPendingApps] = useState([]);
	const [selectedLocation, setSelectedLocation] = useState("All");
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [appToDelete, setAppToDelete] = useState(null);

	const allMarkets = [
		{ value: "atlanta", label: "Atlanta" },
		{ value: "charlotte", label: "Charlotte" },
		{ value: "cincinnati", label: "Cincinnati" },
		{ value: "cleveland", label: "Cleveland" },
		{ value: "columbus", label: "Columbus" },
		{ value: "indianapolis", label: "Indianapolis" },
		{ value: "pittsburgh", label: "Pittsburgh" },
		{ value: "springfield", label: "Springfield" },
		{ value: "tampa_bay", label: "Central Florida" },
	];

	const fetchApplications = async () => {
		const response = await getApplications(userState.accessToken);
		if (response.status === "success") {
			const data = response.data;
			filterLocationApps(data);
			filterPendingApps(data);
			const appYears = Array.from(new Set(data.map(app => new Date(app.createdAt).getFullYear())))
				.filter(y => y !== 1969)
				.sort((a, b) => b - a);
			setYears(["All", ...appYears]);
		}
	};

	const fetchStats = async () => {
		const query = selectedYear !== "All" ? `?year=${selectedYear}` : "";
		const response = await getAllImpactStats(userState.accessToken, query);
		if (response.status === "success") {
			setStats(response.data);
		} else {
			notify("Failed to fetch stats", "error");
		}
	};

	const fetchMarketData = async (market) => {
		const query = selectedYear !== "All" ? `?year=${selectedYear}` : "";
		const response = await getMarketLevelData(userState.accessToken, market, query);
		if (response.status === "success") {
			setMarketData(response.data.appsByMarket);
			filterAppsByLocation(response.data.appsByMarket);
		} else {
			notify("Failed to fetch market data", "error");
		}
	};

	const filterPendingApps = (apps) => {
		const pendingApps = apps.filter(app => app.appStatus === "pending" && app.market === currentMarket);
		setFilteredPendingApps(pendingApps);
	};

	const filterLocationApps = (apps) => {
		const filtered = apps.filter(app => app.market === currentMarket);
		setApps(filtered);
	};

	const filterAppsByLocation = (data) => {
		const result = data.reduce((acc, app) => {
			const loc = app.coLocation || "Unknown";
			if (!acc[loc]) acc[loc] = { id: loc, coLocation: loc, total: 0, pending: 0, approved: 0, rejected: 0 };
			acc[loc].total++;
			acc[loc][app.appStatus] = (acc[loc][app.appStatus] || 0) + 1;
			return acc;
		}, {});
		setFilteredApps(Object.values(result));
	};

	const handleYearChange = (year) => {
		setSelectedYear(year);
	};

	const handleDelete = async (id) => {
		const response = await deleteApplication(id, userState.accessToken);
		if (response.status === "success") {
			notify("Application deleted", "info");
			fetchApplications();
			fetchMarketData(currentMarket);
		} else {
			notify("Error deleting application", "error");
		}
	};

	const StatCard = ({ label, value, color }) => (
		<div className="p-4 bg-white rounded-lg shadow-md">
			<h2 className={`text-4xl font-bold text-${color}`}>{value}</h2>
			<p className="text-gray-600">{label}</p>
		</div>
	);

	const DataGridSection = ({ title, rows, getRowId, columns }) => (
		<section className="bg-white rounded-lg shadow-md p-6">
			<h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
			<Box height="75vh">
				<DataGrid
					rows={rows}
					getRowId={getRowId}
					columns={columns}
					disableRowSelectionOnClick
					sx={{
						'& .MuiDataGrid-columnHeaders': {
							backgroundColor: '#f9fafb', // Light gray background
							color: '#1f2937', // Dark gray text
							fontWeight: 'bold',
							fontSize: '1rem',
							borderBottom: '1px solid #e5e7eb', // Tailwind's gray-200
						},
						'& .MuiDataGrid-columnHeaderTitle': {
							fontWeight: 600,
						},
					}}
				/>
			</Box>
		</section>
	);

	useEffect(() => {
		fetchMarketData(currentMarket);
		fetchStats();
	}, [selectedYear]);

	useEffect(() => {
		fetchMarketData(currentMarket);
		fetchApplications();
	}, [currentMarket]);

	const pieData = [
		{ name: "Renewing Scholars", value: dashstats.totalRenewals || 0, color: "#ffbb28" },
		{ name: "New Scholars", value: dashstats.totalNonRenewals || 0, color: "#00C49F" },
	];

	return (
		<div className="bg-gray-100 rounded-3xl shadow-xl p-6 sm:p-8 space-y-8">

			{/* Filters: Year */}
			<section>
				<h2 className="text-lg font-semibold text-gray-800 mb-2">Filter by Year</h2>
				<div className="flex flex-wrap gap-2">
					{years.map((year) => (
						<button
							key={year}
							onClick={() => handleYearChange(year)}
							className={`px-4 py-2 rounded-md font-medium transition ${selectedYear === year
								? "bg-primary-500 text-white"
								: "bg-gray-200 text-gray-800 hover:bg-primary-500 hover:text-white"
								}`}
						>
							{year}
						</button>
					))}
				</div>
			</section>

			<section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
				<h2 className="text-2xl font-semibold text-gray-800 mb-4 col-span-4">
					Overall Scholarship Application Stats
				</h2>

				{/* Stats Cards */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-3 col-span-3">
					<StatCard
						label="Scholarship Dollars Awarded"
						value={`$${dashstats.annualAward?.[0]?.annualAward?.toLocaleString() || 0}`}
						color="success-800"
					/>
					<StatCard
						label="Active Scholars"
						value={dashstats.totalApprovedApplications || 0}
						color="success-600"
					/>
					<StatCard
						label="Pending Scholars"
						value={dashstats.totalPendingApplications || 0}
						color="yellow-600"
					/>
					<StatCard
						label="Denied Scholars"
						value={dashstats.totalDeniedApplications}
						color="red-600"
					/>
				</div>

				{/* Pie Chart */}
				<div className="p-3 bg-white rounded-lg shadow-md flex justify-center items-center col-span-1">
					<h2 className="text-2xl font-semibold text-gray-800 mb-4 w-full text-center">
						Renewal VS. New Scholars
					</h2>
					<PieChart width={300} height={300}>
						<Pie
							data={pieData}
							cx="50%"
							cy="50%"
							innerRadius={60}
							outerRadius={100}
							dataKey="value"
						>
							<Label
								content={({ viewBox }) => {
									const { cx, cy } = viewBox;
									return (
										<text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
											<tspan x={cx} dy="0">{pieData[0].value} Renewing</tspan>
											<tspan x={cx} dy="1.2em">{pieData[1].value} New</tspan>
										</text>
									);
								}}
							/>
							{pieData.map((entry, index) => (
								<Cell key={`cell-${index}`} fill={entry.color} />
							))}
						</Pie>
						<Tooltip />
					</PieChart>
				</div>
			</section>

			{/* Market Tabs - Moved Below Stats */}
			<section>
				<h2 className="text-lg font-semibold text-gray-800 mb-2">Select Market</h2>
				<div className="flex flex-wrap gap-2">
					{allMarkets.map((market) => (
						<button
							key={market.value}
							onClick={() => setCurrentMarket(market.value)}
							className={`px-4 py-2 rounded-md font-medium transition ${currentMarket === market.value
								? "bg-primary-400 text-white"
								: "bg-gray-200 text-gray-800 hover:bg-primary-400 hover:text-white"
								}`}
						>
							{market.label}
						</button>
					))}
				</div>
			</section>

			{/* Header Section for Market Data */}
			<header className="flex flex-wrap justify-between items-center mb-6">
				<h1 className="text-2xl md:text-2xl font-bold text-gray-800">
					{capitalizeFirstLetter(
						allMarkets.find((market) => market.value === currentMarket)?.label || currentMarket
					)}{" "}
					Location Scholarships
				</h1>
			</header>

			{/* Market Stats Table */}
			<DataGridSection
				title="Market Location Stats"
				rows={filteredApps || []}
				getRowId={(row) => row.id || row.coLocation}
				columns={[
					{ field: "coLocation", headerName: "Location", flex: 1 },
					{ field: "total", headerName: "Total", flex: 1 },
					{ field: "pending", headerName: "Pending", flex: 1 },
					{ field: "approved", headerName: "Approved", flex: 1 },
					{ field: "rejected", headerName: "Denied", flex: 1 },
				]}
			/>

			{/* Location Filter */}
			<section>
				<h2 className="text-xl font-semibold text-gray-800 mb-2">Filter by Location</h2>
				<div className="flex overflow-x-auto space-x-2 no-scrollbar">
					{["All", ...Array.from(new Set(apps.map((app) => app.coLocation || "Unknown"))).sort()].map((location) => {
						const isSelected = selectedLocation === location;
						return (
							<button
								key={location}
								onClick={() => setSelectedLocation(location)}
								className={`whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex-shrink-0 ${isSelected
									? "bg-primary-400 text-white"
									: "bg-gray-100 text-gray-800 hover:bg-primary-400 hover:text-white"
									}`}
							>
								{location}
							</button>
						);
					})}
				</div>
			</section>


			{/* Applications by Location */}
			<DataGridSection
				title="Applications by Location"
				rows={
					selectedLocation === "All"
						? apps
						: apps.filter((app) => app.coLocation === selectedLocation)
				}
				getRowId={(row) => row._id || row.id || `${row.coLocation}-${row.total}`}
				columns={[
					{
						field: "orgName",
						headerName: "Organization",
						flex: 1,
						renderCell: (params) => {
							const type = params.row.type;
							const id = params.row._id;
							const url = `/dashboard/${type === "Community Partner" ? "community-partner" : "profile"}/${id}`;

							return (
								<a
									href={url}
									target="_blank"
									rel="noreferrer"
									className="text-success-800 underline"
								>
									{params.row.orgName}
								</a>
							);
						},
					},
					{ field: "type", headerName: "Type", flex: 1 },
					{ field: "coLocation", headerName: "Location", flex: 1 },
					{ field: "appStatus", headerName: "App Status", flex: 1 },
					{ field: "cmStatus", headerName: "CM Status", flex: 1 },
					{
						field: "createdAt",
						headerName: "Submitted",
						flex: 1,
						valueGetter: ({ value }) => {
							const date = new Date(value);
							return isNaN(date.getTime()) ? "Invalid date" : format(date, "MM/dd/yyyy");
						},
					},
					{
						field: "delete",
						headerName: "Delete",
						flex: 1,
						renderCell: (params) => (
							<button
								onClick={() => {
									setAppToDelete(params.row._id);
									setShowDeleteModal(true);
								}}
								className="text-danger-500 font-semibold"
							>
								Delete
							</button>
						),
					},
				]}
			/>

			{/* Pending Applications */}
			<DataGridSection
				title="Pending Applications"
				rows={filteredPendingApps || []}
				getRowId={(row) => row._id}
				columns={[
					{ field: "orgName", headerName: "Organization", flex: 1 },
					{ field: "type", headerName: "Type", flex: 1 },
					{ field: "coLocation", headerName: "Location", flex: 1 },
					{
						field: "createdAt",
						headerName: "Submitted",
						flex: 1,
						valueGetter: ({ value }) => format(new Date(value), "MM/dd/yyyy"),
					},
					{
						field: "delete",
						headerName: "Delete",
						flex: 1,
						renderCell: (params) => (
							<button
								onClick={() => {
									setAppToDelete(params.row._id);
									setShowDeleteModal(true);
								}}
								className="text-danger-500 font-semibold"
							>
								Delete
							</button>
						),
					},
				]}
			/>
			{showDeleteModal && (
				<div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
					<div className="bg-white p-6 rounded-lg shadow-md w-96">
						<h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
						<p className="mb-6">Are you sure you want to delete this application?</p>
						<div className="flex justify-end space-x-3">
							<button
								onClick={() => {
									setShowDeleteModal(false);
									setAppToDelete(null);
								}}
								className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
							>
								Cancel
							</button>
							<button
								onClick={async () => {
									await handleDelete(appToDelete);
									setShowDeleteModal(false);
									setAppToDelete(null);
								}}
								className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
export default MarketLevel;
